import { makeAutoObservable } from 'mobx';
import { fromPromise, type IPromiseBasedObservable } from 'mobx-utils';
import type PulseApi from '../../../api/PulseApi';
import Account, { type AccountDto } from './Account';
import type Service from './services/Service';

export default class AccountStore {
  account?: Account = undefined;

  constructor(private readonly api: PulseApi) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async loadAccount() {
    this.setAccount(await this.fetchAccount());
  }

  fetchAccount(): IPromiseBasedObservable<Account> {
    return fromPromise(
      this.api.account
        .getAccount()
        .then((dto) => this.createAccountFromDto(dto)),
    );
  }

  fetchPulseCdnOptions() {
    return this.api.account.connectPulseCdn();
  }

  async saveAccount({
    account,
    service,
    reason = 'account',
  }: {
    account?: Account;
    reason?: string;
    service?: Service;
  } = {}) {
    this.setAccount(account);

    if (account && reason === 'labels') {
      await this.api.account.updateLabels(
        account.configurations.labels.toJSON(),
      );
      return;
    }

    if (service) {
      if (reason === 'delete') {
        await this.api.account.deleteService(service.toJSON());
        return;
      }

      await this.api.account.updateServices([service.toJSON()]);
      return;
    }

    if (account) {
      await this.api.account.saveAccount(account.toJSON());
    }
  }

  async switchPulseCdnIntegration(enabled: boolean) {
    await (enabled
      ? this.fetchPulseCdnOptions()
      : this.api.account.deleteService({ provider: 'pulsecdn.net' }));

    await this.loadAccount();

    const bucketBlaze =
      this.account?.configurations.storages.findByProvider('pulsecdn.net');

    bucketBlaze?.handleChange({ name: 'enabled', value: enabled });
  }

  async testEmailTemplate(emailTemplate: string) {
    const body = emailTemplate
      .replaceAll('[products-names]', 'Product Name')
      .replaceAll('[redemption-keys]', 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx');

    return this.api.account.sendEmailToMyself({
      body,
      subject: 'Pulse Email Template Test',
    });
  }

  private setAccount(account?: Account) {
    this.account = account;
  }

  private createAccountFromDto(dto: AccountDto) {
    return new Account(dto, this.saveAccount);
  }
}
